import React from 'react';
import { GeoJson } from 'types/GeoJson'
import { getMessage } from '@tcc/i18n';
import { Icon } from '@tcc/crate';
import { RegionReportStats } from '../RegionReportStats/RegionReportStats';
import { RegionReportFooter } from '../RegionReportFooter/RegionReportFooter';
import { Activity, YieldRegionReport } from '../../../../types';
import styles from '../RegionReport.css';
import { getGeoJson } from '../../../../utils/geo';
import { RegionThumbnailNoteBox } from '../RegionReportThumbnailNoteBox/RegionThumbnailNoteBox';
import { RegionReportSummary } from '../RegionReportSummary/RegionReportSummary';
import { parseAppliedApplicationsData } from '../../../../utils/harvestData';
import { getAverageYieldText } from '../../../../utils/regionUtils';
import { SILAGE_CORN_CROP_CODE} from '../../../../constants/cropCodes';
import {
  getCropNominalWeight,
} from '../../../../utils/processUnits';
import { useAppSelector } from '../../../../utils';


export type RegionReportListProps = {
  activity: Activity;
  cropCode: string;
  data: YieldRegionReport;
  fieldBoundary: GeoJson;
  fieldName: string;
  hasAsAppliedApplications: boolean;
  isAirSeeder: boolean;
  locale: string;
  onCancelClick: () => void;
  onClick: (item: YieldRegionReport) => void;
  onDeleteClick: () => void;
  onRegionButtonClick?: () => void;
  saveClick: () => void;
  showSaveButton: boolean;
  index: number;
};

export const RegionReportList = ({
  activity,
  cropCode,
  data,
  fieldBoundary,
  fieldName,
  hasAsAppliedApplications,
  index,
  isAirSeeder,
  locale,
  onCancelClick,
  onClick,
  onDeleteClick,
  onRegionButtonClick = () => undefined,
  saveClick,
  showSaveButton,
}: RegionReportListProps) => {
  const cropsState = useAppSelector(state => state.crops);
  const { averageYield, area, averageMoisture, totalWeight } = data;
  const cropNominalWeight = getCropNominalWeight(cropCode, cropsState);

  const averageYieldText = getMessage(getAverageYieldText(cropCode));

  const areaUnit =
    area.u === 'ac'
      ? getMessage('regions.report.modal.acre')
      : getMessage('regions.report.modal.hectare');

  const geoJson = activity?.id ?
    getGeoJson([fieldBoundary, activity.location])
    : getGeoJson([fieldBoundary]);

  const isSilageCrop = cropCode === SILAGE_CORN_CROP_CODE

  return (
    <div>
      <h2 className={styles.title} data-test-id="frr-title">
          {getMessage('regions.report.modal.fieldReport')}
        </h2>
      <Icon
        className={styles.closeModalTitle}
        glyph={Icon.set.REMOVE}
        onClick={onCancelClick}
        testId='region-report-list-close-icon'
      />
      {activity &&
        <RegionThumbnailNoteBox
          activity={activity}
          fieldName={fieldName}
          geoJson={geoJson}
          locale={locale}
          onRegionButtonClick={onRegionButtonClick}
        />
      }
      <RegionReportSummary
          averageYield={averageYield}
          totalWeight={totalWeight}
          averageYieldText={averageYieldText}
          area={area}
          averageMoisture={averageMoisture}
          withDryMatterYield={isSilageCrop}
          cropCode={cropCode}
          cropNominalWeight={cropNominalWeight}
        />
      {data.applicationStats
        && data.applicationStats.length > 0
        && hasAsAppliedApplications
        && (
          <RegionReportStats
            areaUnit={areaUnit}
            cropCode={cropCode}
            data={parseAppliedApplicationsData(data.applicationStats)}
            dataTestId="applications" // as applied application
            headerText={getMessage('regions.report.modal.byApplication')}
            index={index}
            locale={locale}
          // onClickItem={onClick} TODO: https://climate.jira.com/browse/YAN-356
          // showIcon
          />
        )}
      {data.hybridStats && data.hybridStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          colors={data.hybridColors}
          cropCode={cropCode}
          data={data.hybridStats}
          dataTestId="hybrids"
          headerText={getMessage('regions.report.modal.byHybrid')}
          index={index}
          onClickItem={onClick}
          showIcon
        />
      )}
      {data.soilStats && data.soilStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={data.soilStats}
          dataTestId="soil"
          headerText={getMessage('regions.report.modal.bySoil')}
          index={index}
          onClickItem={onClick}
          showIcon
        />
      )}
      {!isAirSeeder &&
        data.populationStats &&
        data.populationStats.length > 0 && (
          <RegionReportStats
            allowHover
            areaUnit={areaUnit}
            cropCode={cropCode}
            data={data.populationStats}
            dataTestId="population"
            headerText={getMessage('regions.report.modal.byPopulation')}
            index={index}
            onClickItem={onClick}
            showIcon
            displayRanges
          />
        )}
      {data.elevationStats && data.elevationStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={data.elevationStats}
          dataTestId="elevation"
          headerText={getMessage('regions.report.modal.byElevation')}
          index={index}
          onClickItem={onClick}
          showIcon
          displayRanges
        />
      )}
      {data.planterDateStats && data.planterDateStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={data.planterDateStats}
          dataTestId="planterDate"
          headerText={getMessage('regions.report.modal.byPlanterDate')}
          index={index}
          onClickItem={onClick}
          showIcon
        />
      )}
      {data.seedTreatmentStats && data.seedTreatmentStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={data.seedTreatmentStats}
          dataTestId="seedTreatment"
          headerText={getMessage('regions.report.modal.bySeedTreatment')}
          index={index}
          onClickItem={onClick}
          showIcon
        />
      )}
      <RegionReportFooter
        onCancelClick={onCancelClick}
        onDeleteClick={onDeleteClick}
        saveClick={saveClick}
        showSaveButton={showSaveButton}
      />
    </div>
  );
};

